import React from 'react';

import {
  Info as InfoIcon,
  TriangleAlert as TriangleAlertIcon,
} from 'lucide-react';

import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react';

type BudgetPlanningPreviewStateAlertProps = {
  variant: 'info' | 'error';
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
};

const variantStyles = {
  info: {
    containerBorder: 'blue.100',
    containerBorderLeft: 'blue.300',
    containerBackground: 'blue.50',
    iconColor: 'blue.400',
    buttonBorder: 'blue.200',
    buttonColor: 'blue.600',
    buttonHover: 'blue.100',
    buttonActive: 'blue.200',
    icon: InfoIcon,
  },
  error: {
    containerBorder: 'red.100',
    containerBorderLeft: 'red.300',
    containerBackground: 'red.50',
    iconColor: 'red.400',
    buttonBorder: 'red.200',
    buttonColor: 'red.600',
    buttonHover: 'red.100',
    buttonActive: 'red.200',
    icon: TriangleAlertIcon,
  },
} as const;

const BudgetPlanningPreviewStateAlert: React.FC<
  BudgetPlanningPreviewStateAlertProps
> = ({ variant, title, description, actionLabel, onAction }) => {
  const styles = variantStyles[variant];

  return (
    <Box
      w="100%"
      border="1px solid"
      borderColor={styles.containerBorder}
      borderLeftWidth="3px"
      borderLeftColor={styles.containerBorderLeft}
      borderRadius="8px"
      background={styles.containerBackground}
      p={3}
      mb={3}
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live="polite"
      aria-atomic="true"
    >
      <Flex alignItems="center" justifyContent="space-between" gap={3}>
        <Flex alignItems="center" gap={3} minW={0}>
          <Icon
            as={styles.icon}
            color={styles.iconColor}
            width={4}
            height={4}
          />

          <Box minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              {title}
            </Text>
            <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
              {description}
            </Text>
          </Box>
        </Flex>

        {actionLabel && onAction && (
          <Button
            size="sm"
            variant="outline"
            borderColor={styles.buttonBorder}
            color={styles.buttonColor}
            _hover={{ bg: styles.buttonHover }}
            _active={{ bg: styles.buttonActive }}
            onClick={onAction}
            flexShrink={0}
          >
            {actionLabel}
          </Button>
        )}
      </Flex>
    </Box>
  );
};

export default BudgetPlanningPreviewStateAlert;
